import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import AccessLocalStorage from "../../services/localStorage";
import { getApiUrl } from "../../services/api_service";
import {
    Box, Button, CircularProgress, Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { dashboardText, paddingbox, paddingboxeven, pcolor, rcolor, orangecolor, darkcolor, lightcolor, whitecolor, lightpinkcolor, brownColor, primaryColor } from "../../style2";
import dayjs from "dayjs";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const EMC = {
    teal: "#4EBFB0",
    tealLight: "rgba(78,191,176,0.12)",
    navy: pcolor,                          // #115C71
    navyLight: "rgba(17,92,113,0.10)",
    coral: rcolor,                         // #FF767E
    coralLight: lightpinkcolor,            // #FAE6EC
    gold: orangecolor,                     // #ff914d
    goldLight: "rgba(255,145,77,0.12)",
    green: "#3DAA6E",
    greenLight: "rgba(61,170,110,0.12)",
    bg: lightcolor,                        // #F6F6F6
    cardBg: whitecolor,                    // #FFFFFF
    border: "rgba(17,92,113,0.12)",
    textPrimary: pcolor,                   // #115C71
    textSecondary: darkcolor,              // #545454
    font: "'Plus Jakarta Sans', sans-serif",
};

// ─── Field Colour Mapping ─────────────────────────────────────────────────────
const FIELD_COLORS = {
    shared_concerns: { color: EMC.teal, bg: EMC.tealLight },
    risk_flags: { color: EMC.coral, bg: EMC.coralLight },
    parent_concerns: { color: EMC.navy, bg: EMC.navyLight },
    teacher_concerns: { color: EMC.gold, bg: EMC.goldLight },
    strengths: { color: EMC.green, bg: EMC.greenLight },
    recommendations: { color: EMC.teal, bg: EMC.tealLight },
    concerns: { color: EMC.coral, bg: EMC.coralLight },
    observations: { color: EMC.navy, bg: EMC.navyLight },
    achievements: { color: EMC.green, bg: EMC.greenLight },
};

const DEFAULT_COLORS = [
    { color: EMC.navy, bg: EMC.navyLight },
    { color: EMC.teal, bg: EMC.tealLight },
    { color: EMC.gold, bg: EMC.goldLight },
    { color: EMC.green, bg: EMC.greenLight },
];

const getFieldColor = (key) => {
    const lk = key.toLowerCase();
    for (const [pattern, colors] of Object.entries(FIELD_COLORS)) {
        if (lk.includes(pattern)) return colors;
    }
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
    return DEFAULT_COLORS[Math.abs(hash) % DEFAULT_COLORS.length];
};

const isAlertField = (key) => {
    const lk = key.toLowerCase();
    return lk.includes("discrepancy") || lk.includes("variability") || lk.includes("risk_flag");
};

const humanize = (key) =>
    key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase());

const parseOutput = (output) => {
    if (!output) return {};
    try { return typeof output === "string" ? JSON.parse(output) : output; }
    catch { return { content: String(output) }; }
};

const parseSchema = (outputschema) => {
    if (!outputschema) return {};
    try { return typeof outputschema === "string" ? JSON.parse(outputschema) : outputschema; }
    catch { return {}; }
};

const getOrderedKeys = (schema, parsed) => {
    const schemaKeys = Object.keys(parseSchema(schema));
    const parsedKeys = Object.keys(parseOutput(parsed));
    if (schemaKeys.length > 0) {
        const ordered = schemaKeys.filter(k => parsedKeys.includes(k));
        const extra = parsedKeys.filter(k => !ordered.includes(k));
        return [...ordered, ...extra];
    }
    return parsedKeys;
};

// ─── CardField ────────────────────────────────────────────────────────────────
function CardField({ fieldKey, value, isFirst }) {
    const hasValue = Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== undefined && String(value).trim() !== "";
    if (!hasValue) return null;

    const colors = getFieldColor(fieldKey);

    // Alert-style fields (discrepancy, risk flags, variability)
    if (isAlertField(fieldKey)) {
        const text = Array.isArray(value) ? value.join(" · ") : String(value);
        const isRisk = fieldKey.toLowerCase().includes("risk");
        return (
            <Box sx={{
                background: isRisk ? EMC.coralLight : EMC.goldLight,
                border: `1px solid ${(isRisk ? EMC.coral : EMC.gold)}`,
                borderRadius: "8px", padding: "10px 14px",
                mt: isFirst ? 0 : 1.5, mb: 3
            }}>
                <Typography sx={{
                    fontFamily: EMC.font, fontWeight: 700, fontSize: "0.68rem",
                    color: isRisk ? EMC.coral : EMC.gold,
                    textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4,
                }}>
                    {humanize(fieldKey)}
                </Typography>
                <Typography sx={{ fontFamily: EMC.font, fontSize: "0.82rem", color: EMC.textPrimary, lineHeight: 1.6 }}>
                    {text}
                </Typography>
            </Box>
        );
    }

    // List → pills
    if (Array.isArray(value)) {
        return (
            <Box sx={{ mt: isFirst ? 0 : 1.5 }}>
                <Typography sx={{
                    fontFamily: EMC.font, fontSize: "0.68rem", fontWeight: 700,
                    color: EMC.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.06em", mb: 0.6,
                }}>
                    {humanize(fieldKey)}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.6}>
                    {value.map((item, i) => (
                        <Box key={i} component="span" sx={{
                            background: colors.bg, color: colors.color,
                            border: `1px solid ${colors.color}33`,
                            borderRadius: "20px", padding: "3px 11px",
                            fontFamily: EMC.font, fontSize: "0.78rem", fontWeight: 600,
                            display: "inline-block",
                        }}>
                            {String(item)}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    // Object → key/value list
    if (typeof value === "object" && value !== null) {
        return (
            <Box sx={{ mt: isFirst ? 0 : 1.5 }}>
                <Typography sx={{
                    fontFamily: EMC.font, fontSize: "0.68rem", fontWeight: 700,
                    color: EMC.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.06em", mb: 0.5,
                }}>
                    {humanize(fieldKey)}
                </Typography>
                <Box sx={{ background: colors.bg, borderRadius: "6px", padding: "8px 12px", border: `1px solid ${colors.color}22` }}>
                    {Object.entries(value).map(([k, v]) => (
                        <Typography key={k} sx={{ fontFamily: EMC.font, fontSize: "0.83rem", color: EMC.textPrimary, lineHeight: 1.65 }}>
                            <strong>{humanize(k)}:</strong> {String(v)}
                        </Typography>
                    ))}
                </Box>
            </Box>
        );
    }

    // Text → paragraph
    const displayValue =
        typeof value === "string" && value.length > 220 ? value.slice(0, 220) + "…" : value;
    return (
        <Box sx={{ mt: isFirst ? 0 : 1.5 }}>
            <Typography sx={{
                fontFamily: EMC.font, fontSize: "0.68rem", fontWeight: 700,
                color: EMC.textSecondary, textTransform: "uppercase",
                letterSpacing: "0.06em", mb: 0.4,
            }}>
                {humanize(fieldKey)}
            </Typography>
            <Typography sx={{ fontFamily: EMC.font, fontSize: "0.84rem", color: EMC.textPrimary, lineHeight: 1.65 }}>
                {typeof displayValue === "string" ? displayValue : String(displayValue)}
            </Typography>
        </Box>
    );
}

// ─── PillChip ─────────────────────────────────────────────────────────────────
function PillChip({ label, colors, onClick }) {
    return (
        <Box component="span" onClick={onClick}
            sx={{
                fontSize: "0.78rem", color: colors.color, background: colors.bg,
                border: `0.5px solid ${colors.color}88`,
                borderRadius: "999px", padding: "4px 14px",
                display: "inline-block", fontFamily: EMC.font,
                cursor: onClick ? "pointer" : "default",
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </Box>
    );
}

// ─── ReportCard ───────────────────────────────────────────────────────────────
function ReportCard({ report }) {
    const history = useHistory();
    const parsed = parseOutput(report.output);
    const allKeys = getOrderedKeys(report.outputschema, report.output);

    // Separate alert fields from regular fields for preview
    const regularKeys = allKeys.filter(k => !isAlertField(k));
    const alertKeys = allKeys.filter(k => isAlertField(k));
    const previewRegularKeys = regularKeys.slice(0, 3);
    const extraCount = allKeys.length - previewRegularKeys.length - alertKeys.length;

    const handleViewFull = () => {
        history.push(`/aisummarydashboard/${report.pathwayId}/${report.organizationId}`);
    };

    // Status indicators from triage / recommendation
    const statusItems = [];
    if (report.triageDetails?.triageEnabled) {
        const triageStatus = report.triageDetails?.triageReviewStatus ?? 0;
        switch (triageStatus) {
            case 0:
                statusItems.push({ dot: orangecolor, label: `Triage Pending Review` });
                break;
            case 1:
                statusItems.push({ dot: pcolor, label: `Triage Decision Accepted` });
                break;
            case 2:
                statusItems.push({ dot: brownColor, label: `Triage Decision Overriden` });
                break;
            case 3:
                statusItems.push({ dot: rcolor, label: `Triage Decision Rejected` });
                break;
            default:
                statusItems.push({ dot: rcolor, label: `` });
                break;

        }
    }
    if (report.recommendationSentAt) {
        statusItems.push({ dot: EMC.green, label: `Recommendation sent to family · ${dayjs(report.recommendationSentAt).format("DD/MM/YYYY")}` });
    }

    // Meta line: date · pathway · org
    const metaParts = [
        dayjs(report.createdAt).format("DD/MM/YYYY"),
        report.pathwayName,
        report.organizationName,
    ].filter(Boolean).join(" · ");

    return (
        <Box sx={{
            background: whitecolor,
            border: `0.5px solid ${EMC.border}`,
            borderRadius: "12px",
            padding: "1.25rem 1.5rem",
            fontFamily: EMC.font,
            mb: 2,
        }}>
            {/* Top row: meta + status badge */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1.5}>
                <Typography sx={{ fontFamily: EMC.font,  color: EMC.pcolor, m: 0 }}>
                    {metaParts}
                </Typography>
                {report.isPublished && (
                    <Box component="span" sx={{
                        fontSize: "0.75rem", fontWeight: 500, color: EMC.green,
                        background: EMC.greenLight, border: `0.5px solid ${EMC.teal}`,
                        borderRadius: "999px", padding: "3px 14px", whiteSpace: "nowrap",
                        fontFamily: EMC.font,
                    }}>
                        Published
                    </Box>
                )}
            </Box>

            {/* Preview fields */}
            {previewRegularKeys.map((key) => {
                const value = parsed[key];
                const hasValue = Array.isArray(value) ? value.length > 0
                    : value !== null && value !== undefined && String(value).trim() !== "";
                if (!hasValue) return null;

                const colors = getFieldColor(key);

                if (Array.isArray(value)) {
                    const visible = value.slice(0, 3);
                    const extra = value.length - 3;
                    return (
                        <Box key={key} mb={1.5}>
                            <Typography sx={{
                                color: primaryColor,
                                textTransform: "uppercase", mb: 1, mt: 3,
                            }}>
                                {humanize(key)}
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap="8px">
                                {visible.map((item, i) => (
                                    <PillChip key={i} label={String(item)} colors={colors} />
                                ))}
                                {extra > 0 && (
                                    <PillChip label={`+${extra} more`} colors={colors} onClick={handleViewFull} />
                                )}
                            </Box>
                        </Box>
                    );
                }

                // Text / object
                const text = typeof value === "object" && value !== null
                    ? Object.entries(value).map(([k, v]) => `${humanize(k)}: ${v}`).join(" · ")
                    : typeof value === "string" && value.length > 180 ? value.slice(0, 180) + "…"
                        : String(value);

                return (
                    <Box key={key} mb={1.5}>
                        <Typography sx={{
                            fontFamily: EMC.font, color: primaryColor,
                            textTransform: "uppercase", mb: 1, mt: 3,
                        }}> 
                            {humanize(key)} sd f
                        </Typography>
                        <Typography sx={{ fontFamily: EMC.font, color: EMC.textPrimary }}>
                            {text}
                        </Typography>
                    </Box>
                );
            })}

            {/* Alert fields inline */}
            {alertKeys.map((key) => {
                const value = parsed[key];
                const hasValue = Array.isArray(value) ? value.length > 0
                    : value !== null && value !== undefined && String(value).trim() !== "";
                if (!hasValue) return null;
                const text = Array.isArray(value) ? value.join(" · ") : String(value);
                const isRisk = key.toLowerCase().includes("risk");
                return (
                    <Box key={key} mb={1.5} sx={{
                        background: isRisk ? lightpinkcolor : EMC.goldLight,
                        border: `0.5px solid ${isRisk ? rcolor : orangecolor}44`,
                        borderRadius: "8px", padding: "8px 12px", mt: 3
                    }}>
                        <Typography sx={{ fontFamily: EMC.font, color: isRisk ? rcolor : orangecolor, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>
                            {humanize(key)}
                        </Typography>
                        <Typography sx={{ fontFamily: EMC.font, color: EMC.textPrimary, lineHeight: 1.6 }}>
                            {text}
                        </Typography>
                    </Box>
                );
            })}

            {/* +N more fields link */}
            {extraCount > 0 && (
                <Typography onClick={handleViewFull} sx={{
                    fontFamily: EMC.font, fontSize: "0.84rem", color: EMC.green,
                    mb: "1.25rem", cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                }}>
                    +{extraCount} more field{extraCount > 1 ? "s" : ""}...
                </Typography>
            )}

            {/* Footer */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}
                sx={{ borderTop: `0.5px solid ${EMC.border}`, pt: "1rem" }}
            >
                {/* Status indicators */}
                <Box display="flex" flexDirection="column" gap="6px">
                    {statusItems.map((s, i) => (
                        <Box key={i} display="flex" alignItems="center" gap="8px">
                            <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: EMC.font, fontSize: "0.84rem", color: s.dot }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>

                <Button onClick={handleViewFull}
                    sx={{
                        fontFamily: EMC.font, fontWeight: 500, fontSize: "0.84rem",
                        textTransform: "none", background: pcolor, color: whitecolor,
                        border: "none", borderRadius: "999px", padding: "10px 20px",
                        boxShadow: "none", whiteSpace: "nowrap",
                        "&:hover": { background: EMC.navy, boxShadow: "none" },
                    }}
                >
                    View Ariva AI dashboard
                </Button>
            </Box>
        </Box>
    );
}

// ─── FullField / FullReportCard have been moved to aisummarydashboard.js ──────

// ─── AISummaryList ────────────────────────────────────────────────────────────
export function AISummaryList({ pathway, fullWidth = false }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState([]);
    const ls = new AccessLocalStorage();

    useEffect(() => {
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            setError("Unauthorized. No token available.");
            return;
        }

        setLoading(true);

        // Build the API URL, append pathwayId if provided
        let apiUrl = `${getApiUrl()}/AISummary/${ls.getCurrentPatientId()}`;
        if (pathway?.id) {
            const sep = apiUrl.includes("?") ? "&" : "?";
            apiUrl += `${sep}p=${encodeURIComponent(pathway.id)}`;
        }

        fetch(apiUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                if (response.status === 200) {
                    response.json().then((data) => {
                        if (data !== null) {
                            setResult(Array.isArray(data) ? data : [data]);
                        }
                    });
                } else {
                    setResult([]);
                    response.json()
                        .then(() => setError("An error occurred."))
                        .catch((err) => { 
                            console.log(err.message); 
                            setError("No published summary available."); });
                }
            })
            .catch((err) => {
                 console.log(err.message);
                  setError(err.message || "An error occurred."); })
            .finally(() => setLoading(false));
    }, [pathway]);

    return (
        <Box sx={{ paddingTop: "15px", ...(fullWidth ? paddingboxeven : paddingbox) }}>
            {/* Section heading */}
            <Box display="flex" alignItems="center" gap={1} sx={{ ...dashboardText, mb: 2 }}>
                {/* <AutoAwesomeIcon sx={{ color: EMC.teal, fontSize: 26 }} /> */}
                {pathway?.name} Ariva AI Summary
                {loading && <CircularProgress size={20} sx={{ color: EMC.teal, ml: 1 }} />}
            </Box>

            {error && (
                <Box sx={{ background: EMC.coralLight, border: `1px solid ${EMC.coral}44`, borderRadius: "10px", padding: "12px 18px", mb: 2 }}>
                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.85rem", color: EMC.coral, fontWeight: 600 }}>
                        {error}
                    </Typography>
                </Box>
            )}

            {!loading && result.length === 0 && !error && (
                <Box sx={{
                    background: EMC.bg, border: `1px dashed ${EMC.border}`, borderRadius: "14px",
                    padding: "40px", textAlign: "center",
                }}>
                    <AutoAwesomeIcon sx={{ color: EMC.textSecondary, fontSize: 36, mb: 1, opacity: 0.4 }} />
                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.9rem", color: EMC.textSecondary }}>
                        No {pathway?.name} summary available.
                    </Typography>
                </Box>
            )}

            {result.map((report) => (
                <ReportCard
                    key={report.id}
                    report={report}
                />
            ))}
        </Box>
    );
}
